import { useEffect, useMemo, useState } from 'react';

import { Search as SearchIcon } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { MATCHED_LABEL, type SearchResultItem, searchResidents } from '@/lib/api/search';

const DEBOUNCE_MS = 250;

/**
 * 의뢰서 4-6 통합 검색 입력 (P9-2).
 *
 * - MUI Autocomplete + 디바운스 250ms
 * - 옵션: 호실 · 환자번호 · 생년월일 · 이름 + 매칭 필드 칩 + BGT 점수
 * - 옵션 클릭 → /residents?detail=<id>&tab=today (P4-1 URL state 재사용)
 * - 검색어 1자 미만이면 호출하지 않음
 */
export function GlobalSearch() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [input]);

  const enabled = debounced.length > 0;
  const { data, isFetching } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => searchResidents(debounced),
    enabled,
    staleTime: 30 * 1000,
  });

  const options = useMemo<SearchResultItem[]>(() => data?.items ?? [], [data]);

  return (
    <Autocomplete<SearchResultItem, false, false, true>
      freeSolo
      size="small"
      sx={{ width: { xs: '100%', md: 360 } }}
      inputValue={input}
      onInputChange={(_, value) => setInput(value)}
      options={options}
      filterOptions={(x) => x}
      loading={enabled && isFetching}
      noOptionsText={
        !enabled
          ? '이름, 호실, 환자번호, 생년월일로 검색'
          : isFetching
            ? '검색 중…'
            : '일치하는 결과가 없습니다'
      }
      getOptionLabel={(opt) => {
        if (typeof opt === 'string') return opt;
        const parts: string[] = [];
        if (opt.room) parts.push(`${opt.room.roomNumber}호`);
        if (opt.user?.patientCode) parts.push(opt.user.patientCode);
        if (opt.user?.name) parts.push(opt.user.name);
        return parts.join(' · ') || opt.residentId.slice(0, 8);
      }}
      isOptionEqualToValue={(opt, v) =>
        typeof opt !== 'string' && typeof v !== 'string' && opt.residentId === v.residentId
      }
      onChange={(_, value) => {
        if (typeof value === 'string' || value === null) return;
        navigate(`/residents?detail=${value.residentId}&tab=today`);
        setInput('');
      }}
      renderOption={(props, opt) => {
        const room = opt.room ? `${opt.room.roomNumber}호` : '미배정';
        const code = opt.user?.patientCode ?? '—';
        const name = opt.user?.name;
        const birth = opt.user?.birthDate ?? '—';
        const score = opt.bgtSummary?.avgScore;
        return (
          <Box
            component="li"
            {...props}
            key={opt.residentId}
            sx={{ alignItems: 'flex-start !important' }}
          >
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={700}>
                  {room}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {code}
                </Typography>
                {name && (
                  <Typography variant="caption" color="text.secondary">
                    · {name}
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.disabled">
                  생년월일 {birth}
                </Typography>
                {score !== null && score !== undefined && (
                  <Typography variant="caption" color="text.secondary">
                    · BGT {score}점
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Chip
              size="small"
              label={MATCHED_LABEL[opt.matchedBy]}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem', ml: 1 }}
            />
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="이름 · 호실 · 환자번호 · 생년월일"
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.default',
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: 'divider' },
              '&.Mui-focused fieldset': { borderColor: 'primary.main' },
            },
          }}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {enabled && isFetching ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
